import { useEffect, useRef } from 'react';

/**
 * JS-driven marquee (requestAnimationFrame) — DailyTrade-style behavior.
 *
 * Why JS, why not CSS? — A CSS animation stops by itself when the OS has the
 * "reduce motion" setting on. On Windows, with Settings → Accessibility →
 * Visual effects → "Animation effects" OFF, the browser reports
 * `prefers-reduced-motion: reduce` and our old CSS animation STOPPED there
 * (that's why reviews looked "frozen" on some laptops, while DailyTrade's
 * reviews keep moving — their marquee runs on JS). This hook keeps reviews
 * moving on every system; it pauses on mouse hover or keyboard focus.
 *
 * @param {number} speedPxPerSec - speed (homepage ~45, reviews page ~32)
 * @param {boolean} reverse - true = left-to-right instead of right-to-left
 */
export function useMarquee(speedPxPerSec = 45, reverse = false) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const st = { x: 0, paused: false, raf: 0, last: 0 };

    // Track mein content ki 2 copies hain → aadhi width par wrap = seamless loop.
    // The reverse row starts at -half so there is content on both sides.
    const alignReverse = () => {
      const half = el.scrollWidth / 2;
      if (reverse && half > 0 && st.x === 0) st.x = -half;
    };

    const step = (now) => {
      const dt = Math.min((now - (st.last || now)) / 1000, 0.1); // no jump when returning to the tab
      st.last = now;
      if (!st.paused && dt > 0) {
        const half = el.scrollWidth / 2;
        if (half > 0) {
          st.x += (reverse ? 1 : -1) * speedPxPerSec * dt;
          if (!reverse && st.x <= -half) st.x += half;
          if (reverse && st.x >= 0) st.x -= half;
          el.style.transform = `translate3d(${st.x}px, 0, 0)`;
        }
      }
      st.raf = requestAnimationFrame(step);
    };

    alignReverse();
    const t1 = setTimeout(alignReverse, 800); // re-measure after fonts load
    const onEnter = () => { st.paused = true; };
    const onLeave = () => { st.paused = false; };
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focusin', onEnter);
    el.addEventListener('focusout', onLeave);
    st.raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(st.raf);
      clearTimeout(t1);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('focusin', onEnter);
      el.removeEventListener('focusout', onLeave);
    };
  }, [speedPxPerSec, reverse]);

  return ref;
}
